/**
 * Die Rechenregeln der Auswahl auf der Gast-Angebotsseite (Zubuchungen, ersetzende Zubuchungen,
 * Mindestumsatz, Wahlgang-Mengen). REINE Funktionen, kein React.
 *
 * WICHTIG: Hier steht eine VORSCHAU. Verbindlich ist der Server (`angenommener_betrag_aus_snapshot`,
 * sql/752): er addiert und subtrahiert dieselben beim Versand eingefrorenen Ganzzahlen. Der Browser
 * bekommt diese Ganzzahlen mit (`addon_price_cents`, `addon_unit_price_cents`,
 * `hauptmenue_ersetzung.abzugCents[k]`) und rechnet NIE einen Preis selbst — er wendet nur
 * dieselbe Addition/Subtraktion an. Nichts hier ist an einen Betrieb, ein Gericht oder eine Zahl
 * gebunden: alles kommt aus dem Angebot.
 */
import type { PublicOffer, PublicOfferItem } from "./types";
import { AUFSTOCKUNG_MINDESTUMSATZ_VAT_BP } from "@maestro/pricing";

export type AngebotOption = PublicOffer["options"][number];

/* ───────────── Gänge und Alternativen (aus offer-public.tsx hierher verlegt) ───────────── */

/**
 * Punkt 53: bündelt AUFEINANDERFOLGENDE Positionen mit derselben Gang-Bezeichnung zu einem Block.
 * Nur konsekutiv, nie umsortierend — die Reihenfolge des Angebots ist die des Betriebs.
 */
export function gaengeAus<T extends { courseLabel?: string | null }>(lines: T[]): { label: string | null; lines: T[] }[] {
  const blocks: { label: string | null; lines: T[] }[] = [];
  for (const line of lines) {
    const label = line.courseLabel?.trim() ? line.courseLabel.trim() : null;
    const letzter = blocks[blocks.length - 1];
    if (letzter && letzter.label === label) letzter.lines.push(line);
    else blocks.push({ label, lines: [line] });
  }
  return blocks;
}

/**
 * Migration 450: innerhalb eines Gangs bilden aufeinanderfolgende Positionen derselben Wahlgruppe
 * eine Alternative. Eine Gruppe mit nur EINEM Mitglied zählt als normale Position.
 */
export function alternativenAus<T extends { choiceGroupId?: string | null }>(lines: T[]): { alternativen: boolean; lines: T[] }[] {
  const blocks: { gruppe: string | null; lines: T[] }[] = [];
  for (const line of lines) {
    const gruppe = line.choiceGroupId ?? null;
    const letzter = blocks[blocks.length - 1];
    if (gruppe != null && letzter && letzter.gruppe === gruppe) letzter.lines.push(line);
    else blocks.push({ gruppe, lines: [line] });
  }
  return blocks.map((b) => ({ alternativen: b.gruppe != null && b.lines.length > 1, lines: b.lines }));
}

/* ───────────── Speisekarte: Gang, Gerichtname, Beschreibung (nur Anzeige) ───────────── */

/**
 * V-1262 (27.10.2026): Positionen eines Pakets kommen oft als lange Freitexte an, z. B.
 * „Hauptgericht Fisch: Filet von der Dorade Rose (Wildfang) mit Orangencreme und Wintergemüse“.
 * Die Gastseite zeigt sie wie eine Speisekarte: Gang klein darüber, Gerichtname groß, Rest klein.
 * Die Regeln lesen nur die Form des Textes, nie feste Namen. Sie ändern NICHTS an Daten oder Beträgen.
 */
export interface GerichtTeile {
  /** Gang-Bezeichnung: `course_label`, sonst das Präfix vor dem ersten Doppelpunkt. */
  gang: string | null;
  /** Großer Gerichtname. `null` = der Text hat keinen kurzen Namen, er erscheint nur klein. */
  titel: string | null;
  /** Kleine Beschreibung darunter. */
  text: string | null;
}

/** Ein Gerichtname, der länger ist, wird nicht groß gesetzt (keine Textwüste in Serifenschrift). */
export const TITEL_MAX = 56;
/** Erst ab dieser Länge wird ein Name in Titel und Beschreibung geteilt. */
const TEILEN_AB = 48;

/**
 * Bezeichnungen von GÄNGEN (keine Gerichte): steht eines davon als erstes Wort einer Position ohne Doppelpunkt,
 * ist es der Gang („Suppe Champagner-Kastaniencremesuppe …“ → Gang „Suppe“). Deutsch und italienisch, wie in Menüs üblich.
 */
const GANG_WOERTER = new Set(
  ["aperitif", "aperitivo", "amuse-bouche", "suppe", "vorspeise", "vorspeisen", "antipasto", "antipasti", "zwischengang", "primo", "hauptgang", "hauptgericht", "secondo", "dolce", "dessert", "nachspeise", "nachtisch"].map((w) => w.toLocaleLowerCase("de-DE")),
);

const ohneListenzeichen = (s: string): string => s.replace(/^[\s\-–—•·]+/, "").trim();
const norm = (s: string | null | undefined): string => (s ?? "").replace(/\s+/g, " ").trim().toLocaleLowerCase("de-DE");

/** Präfix vor „:“ — höchstens 5 Wörter, kein Komma, kein Satzende (sonst ist es kein Gang, sondern Text). */
export function praefixAus(name: string): { praefix: string; rest: string } | null {
  const m = /^([^:\n]{2,40}):\s*([\s\S]+)$/.exec(name.trim());
  if (!m) return null;
  const praefix = m[1]!.trim();
  const rest = ohneListenzeichen(m[2]!);
  // Eine führende Ordnungszahl („1. Gang …“) ist kein Satzende.
  const ohneZahl = praefix.replace(/^\d+\.\s*/, "");
  if (!rest || praefix.includes(",") || /[.!?]\s+\S/.test(ohneZahl) || praefix.split(/\s+/).length > 5) return null;
  return { praefix, rest };
}

/** Teilt einen langen Namen in Gerichtname + Beschreibung: Satzende, sonst Gedankenstrich, sonst „ mit “. */
function teile(rest: string): { titel: string; text: string | null } {
  const zeilen = rest.split(/\n+/).map((z) => ohneListenzeichen(z)).filter(Boolean);
  if (zeilen.length > 1) {
    const [erste, ...weitere] = zeilen;
    const t = teile(erste!);
    return { titel: t.titel, text: [t.text, ...weitere].filter(Boolean).join(" · ") };
  }
  const s = zeilen[0] ?? rest.trim();
  if (s.length <= TEILEN_AB) return { titel: s, text: null };
  // Satzende: Punkt nach einem Wort mit mindestens 4 Buchstaben, danach kein Kleinbuchstabe (keine Abkürzung „Fl.“, „p.“).
  const satz = /(\p{L}{4,})\.\s+(?!\p{Ll})/u.exec(s);
  if (satz && satz.index + satz[1]!.length >= 8) {
    const ende = satz.index + satz[1]!.length;
    return { titel: s.slice(0, ende), text: s.slice(ende + 1).trim() || null };
  }
  const strich = /\s[–—-]\s/.exec(s);
  if (strich && strich.index >= 8) return { titel: s.slice(0, strich.index).trim(), text: s.slice(strich.index + strich[0].length).trim() || null };
  const mit = / mit /.exec(s);
  if (mit && mit.index >= 8) return { titel: s.slice(0, mit.index).trim(), text: s.slice(mit.index + 1).trim() };
  return { titel: s, text: null };
}

/** Gang, Gerichtname und Beschreibung EINER Position (ohne Blick auf die Nachbarn). */
export function gerichtTeile(name: string, description: string | null | undefined, courseLabel: string | null | undefined): GerichtTeile {
  const label = courseLabel?.trim() ? courseLabel.trim() : null;
  const roh = ohneListenzeichen(name ?? "");
  const p = praefixAus(roh);
  // Steht in der Beschreibung dasselbe wie im Namen (getrimmt, ohne Groß-/Kleinschreibung; V-1011 zeigte jeden Gang doppelt),
  // zählt nur der Name.
  const gleicherText = (x: string | null | undefined): boolean => norm(x) !== "" && (norm(x) === norm(name) || norm(x) === norm(roh) || (p != null && norm(x) === norm(p.rest)));
  description = gleicherText(description) ? null : description;
  let gang = label;
  let rest = roh;
  if (p) {
    // Mit gepflegtem Gang nur dann abschneiden, wenn das Präfix genau der Gang ist (sonst bleibt es Teil des Namens).
    if (!label) {
      // Ein Präfix vor einer „·“-Liste ist der Name des Gerichts, nicht der Gang („Burrata & Parma: Burrata-Crème · …“).
      if ((p.rest.match(/ · /g) ?? []).length >= 2) {
        const titel = p.praefix.replace(/^\d+\.\s*/, "").trim() || p.praefix;
        return { gang: null, titel: titel.length <= TITEL_MAX ? titel : null, text: [titel.length <= TITEL_MAX ? null : p.praefix, p.rest, description?.trim() || null].filter(Boolean).join(" · ") || null };
      }
      gang = p.praefix;
      rest = p.rest;
    } else if (norm(p.praefix) === norm(label)) {
      rest = p.rest;
    }
  } else if (!label) {
    // V-1262: „Suppe Champagner-Kastaniencremesuppe …“ - ein Gang-Wort am Anfang ohne Doppelpunkt ist ebenfalls der Gang.
    // Danach muss ein Gericht folgen (Großbuchstabe), kein Satz („Hauptgang bitte spätestens …“ bleibt Hinweistext).
    // Mit Gedankenstrich: „Vorspeise – Vitello Tonnato …“ (Storia-Pakete, Freitext mit dem Gang im Namen).
    const md = /^(\S+)\s+[–—-]\s+(\S[\s\S]{7,})$/u.exec(roh);
    if (md && GANG_WOERTER.has(norm(md[1]))) {
      gang = md[1]!;
      rest = md[2]!.trim();
    }
    const m = /^(\S+)\s+(\p{Lu}[\s\S]{7,})$/u.exec(roh);
    if (!gang && m && GANG_WOERTER.has(norm(m[1]))) {
      gang = m[1]!;
      rest = m[2]!.trim();
    }
  }
  const beschreibung = description && description.trim() ? description.trim() : null;
  const t = teile(rest);
  const text = [t.text, beschreibung].filter(Boolean).join(" · ") || null;
  // Kein kurzer Name: der ganze Text klein, ungeteilt (nicht an „ mit “ zerschnitten).
  if (t.titel.length > TITEL_MAX) return { gang, titel: null, text: [rest.replace(/\s*\n+\s*/g, " · "), beschreibung].filter(Boolean).join(" · ") };
  return { gang, titel: t.titel, text };
}

/** Eine Zeile der Speisekarte: die Position plus ihre Anzeige-Teile. `art` = was vom Präfix nach dem Gang übrig bleibt. */
export interface KartenZeile<T> {
  it: T;
  courseLabel: string | null;
  choiceGroupId: string | null;
  teile: GerichtTeile;
  /** z. B. „Fisch“ bei „Hauptgericht Fisch“ im Gang „Hauptgericht“ (nur bei „oder“-Gruppen mit gemeinsamem Gang). */
  art: string | null;
}

/**
 * Baut die Zeilen der Speisekarte. Der Gang einer „oder“-Gruppe ist der GEMEINSAME Wortanfang ihrer Präfixe
 * („Hauptgericht Fisch“/„Hauptgericht Fleisch“ → Gang „Hauptgericht“, Art „Fisch“/„Fleisch“) — so bleibt eine
 * Gruppe immer in EINEM Gang zusammen. Ein gepflegter `course_label` hat immer Vorrang.
 */
export function speisekarteZeilen<T extends { name: string; description: string | null; course_label: string | null; choice_group_id?: string | null }>(
  items: T[],
  name: (it: T) => string = (it) => it.name,
  beschreibung: (it: T) => string | null = (it) => it.description,
): KartenZeile<T>[] {
  const zeilen: KartenZeile<T>[] = items.map((it) => {
    const label = it.course_label?.trim() ? it.course_label.trim() : null;
    const t = gerichtTeile(name(it), beschreibung(it), label);
    return { it, courseLabel: t.gang, choiceGroupId: it.choice_group_id ?? null, teile: t, art: null };
  });
  // „oder“-Gruppen (aufeinanderfolgend, dieselbe Gruppe) ohne gepflegten Gang: gemeinsamer Gang.
  let i = 0;
  while (i < zeilen.length) {
    const g = zeilen[i]!.choiceGroupId;
    let j = i + 1;
    while (g != null && j < zeilen.length && zeilen[j]!.choiceGroupId === g) j += 1;
    const block = zeilen.slice(i, j);
    if (block.length > 1 && block.every((z) => !z.it.course_label?.trim())) {
      const woerter = block.map((z) => (z.teile.gang ?? "").split(/\s+/).filter(Boolean));
      const gemeinsam: string[] = [];
      for (let k = 0; woerter.every((w) => w.length > k) && woerter.every((w) => norm(w[k]) === norm(woerter[0]![k])); k += 1) gemeinsam.push(woerter[0]![k]!);
      const gang = gemeinsam.length > 0 ? gemeinsam.join(" ") : null;
      block.forEach((z, k) => {
        const uebrig = woerter[k]!.slice(gemeinsam.length).join(" ");
        z.art = uebrig || null;
        z.courseLabel = gang;
      });
    }
    i = j;
  }
  // Gang aus dem Gerichtnamen (Gestaltung 28.10.2026): eine Zeile ohne Gang, deren Gerichtname ein Gang-Wort als Wort
  // oder Wortende trägt („Festlicher Weihnachtsaperitif …“ → „Aperitif“), bekommt diesen Gang als Überschrift. Nur bei
  // einem kurzen Gerichtnamen (Titel) und außerhalb von „oder“-Gruppen; Hinweistexte ohne Titel bleiben ohne Gang.
  for (const z of zeilen) {
    if (z.courseLabel || z.choiceGroupId || !z.teile.titel) continue;
    const gang = gangAusTitel(z.teile.titel);
    if (gang) {
      z.courseLabel = gang;
      z.teile = { ...z.teile, gang };
    }
  }
  return zeilen;
}

/** Gang-Wort am Ende eines Wortes im Titel („Weihnachtsaperitif“ → „Aperitif“), sonst `null`. */
export function gangAusTitel(titel: string): string | null {
  for (const wort of titel.split(/[\s\-–—/]+/)) {
    const w = norm(wort.replace(/[^\p{L}]/gu, ""));
    if (w.length < 4) continue;
    for (const g of GANG_WOERTER) {
      if (w === g || (w.endsWith(g) && w.length - g.length >= 3)) return g.charAt(0).toLocaleUpperCase("de-DE") + g.slice(1);
    }
  }
  return null;
}

/**
 * Beschreibung als Aufzählung (Gestaltung 28.10.2026): „·“-Listen werden Listenpunkte; sonst wird ein Text, dessen erster
 * Satz eine Komma-Aufzählung aus mindestens drei kurzen Teilen ist („Prosecco-Empfang, 1 Fl. Wasser je 2 Gäste, …“), in
 * Punkte geteilt, kurze Folgesätze, die mit einer Zahl beginnen („1 Kaffee p. Person.“), kommen dazu. Fließtext-Sätze
 * bleiben als `rest` stehen. Ergibt sich keine Liste (unter drei Punkten), ist `punkte` leer und `rest` der ganze Text.
 */
export function aufzaehlungAus(text: string | null | undefined): { punkte: string[]; rest: string | null } {
  const s = (text ?? "").replace(/\s+/g, " ").trim();
  if (!s) return { punkte: [], rest: null };
  const punktListe = s.split(/\s+·\s+/).map((x) => ohneListenzeichen(x)).filter(Boolean);
  if (punktListe.length >= 3) return { punkte: punktListe, rest: null };
  const saetze = s.split(/(?<=\p{L}{4,}\.)\s+(?=[\p{Lu}\d])/u);
  const erster = saetze[0]!.replace(/\.$/, "");
  const teile = erster.split(/,\s+/).map((x) => x.trim()).filter(Boolean);
  if (teile.length < 3 || teile.some((x) => x.length > 80)) return { punkte: [], rest: s };
  const punkte = [...teile];
  const rest: string[] = [];
  for (const satz of saetze.slice(1)) {
    if (rest.length === 0 && /^\d/.test(satz) && satz.split(/\s+/).length <= 8) punkte.push(satz.replace(/\.$/, ""));
    else rest.push(satz);
  }
  return { punkte, rest: rest.length > 0 ? rest.join(" ") : null };
}

/** Ist die Position nur die Wiederholung des Kartentitels (Kopfzeile eines Pakets)? Dann zeigt die Karte sie nicht doppelt. */
export function wiederholtTitel(name: string, titel: string | null | undefined): boolean {
  return !!titel && norm(name) !== "" && norm(name) === norm(titel);
}

/**
 * V-1262: Bestandteile eines ZUBUCHBAREN Pakets (gleiche `package_id` wie eine buchbare Kopfzeile, selbst nicht
 * optional) gehören in die Kachel dieser Zubuchung, nicht in die Speisekarte der Karte.
 * Ergebnis: je Zubuchungs-Kennung ihre Bestandteile (Reihenfolge des Angebots) und die Menge aller verschobenen Positionen.
 */
export function paketBestandteile<T extends { id: string; package_id?: string | null; is_optional: boolean }>(
  items: readonly T[],
  buchbareIds: ReadonlySet<string>,
): { nachZubuchung: Map<string, T[]>; verschoben: Set<string> } {
  const kopfNachPaket = new Map<string, string>();
  for (const it of items) if (it.package_id && buchbareIds.has(it.id) && !kopfNachPaket.has(it.package_id)) kopfNachPaket.set(it.package_id, it.id);
  const nachZubuchung = new Map<string, T[]>();
  const verschoben = new Set<string>();
  for (const it of items) {
    if (!it.package_id || buchbareIds.has(it.id) || it.is_optional) continue;
    const kopf = kopfNachPaket.get(it.package_id);
    if (!kopf) continue;
    nachZubuchung.set(kopf, [...(nachZubuchung.get(kopf) ?? []), it]);
    verschoben.add(it.id);
  }
  return { nachZubuchung, verschoben };
}

/** Punkt 61: wie viele Wahlgruppen der Gast (nach dem alten Einzelwahl-Modell) noch nicht entschieden hat. */
export function offeneWahlgruppen<T extends { courseLabel?: string | null; choiceGroupId?: string | null }>(
  lines: T[],
  wahl: Record<string, string>,
): number {
  let offen = 0;
  for (const gang of gaengeAus(lines)) {
    for (const block of alternativenAus(gang.lines)) {
      const gruppe = block.alternativen ? (block.lines[0]?.choiceGroupId ?? null) : null;
      if (gruppe && !wahl[gruppe]) offen += 1;
    }
  }
  return offen;
}

/* ───────────── Zubuchungen ───────────── */

/**
 * Die vier Arten einer Zubuchung — sie ergeben sich aus den Feldern der Position, nicht aus einem Namen:
 *  - `alle`          pro Person, ein Schalter „für alle N Gäste“ (price_mode per_person, Menge immer 1)
 *  - `zaehler`       pro Einheit/Person mit Zähler 0..max (addon_max_qty > 1) — auch eine per_person-Zubuchung,
 *                    deren Betragsreihe der Versand eingefroren hat: ERSETZEND (sql/759, V-1262, „3 Gäste
 *                    vegetarisch statt Hauptmenü“) oder NICHT ersetzend (sql/763, „7 Gäste anderes Menü“)
 *  - `pauschal`      ein fester Betrag, ein Schalter (Menge 1)
 *  - `mindestumsatz` Mindestumsatz inkl. Menü: gebucht wird nur die Aufstockung (sql/753)
 */
export type ZubuchArt = "alle" | "zaehler" | "pauschal" | "mindestumsatz";

export interface Zubuch {
  id: string;
  item: PublicOfferItem;
  art: ZubuchArt;
  /** `alle`/`mindestumsatz`: der Zeilenbetrag; `zaehler`/`pauschal`: der Betrag EINER Einheit. */
  cents: number;
  /** Obergrenze der Menge (`zaehler`); sonst 1. */
  max: number;
  ersetzt: boolean;
  /** `alle` + ersetzend: wie viele Gäste die Wahl ersetzt (eingefroren). */
  ersetztGaeste: number | null;
  vatBp: number | null;
  gruppe: string | null;
  /** sql/759/763: Gäste-Zähler (ersetzend oder nicht) — eingefrorener Betrag je Gästezahl k (`[k]`, k = 0..max). */
  gaesteCents: number[] | null;
}

/** Buchbar ist eine Position nur, wenn sie optional IST und einen eingefrorenen Preis trägt (sql/480). */
export function zubuchungenAus(o: AngebotOption | null | undefined): Zubuch[] {
  const liste: Zubuch[] = [];
  for (const it of o?.items ?? []) {
    if (it.is_optional !== true) continue;
    const einheit = typeof it.addon_unit_price_cents === "number" ? it.addon_unit_price_cents : 0;
    const zeile = typeof it.addon_price_cents === "number" ? it.addon_price_cents : 0;
    if (einheit <= 0 && zeile <= 0) continue;
    // sql/759: ersetzende per_person-Zubuchung mit eingefrorener Reihe = Gäste-Zähler (ältere Fassungen: Schalter).
    // sql/763: JEDE per_person-Zubuchung mit eingefrorener Reihe (`addon_gaeste_cents`) ist ein Gäste-Zähler 0..N - auch
    // „anderes Menü“, Getränke usw. (Wunsch Domenico, 28.10.2026). Ohne Reihe (Fassung vor sql/763): Schalter „für alle“.
    const reihe =
      it.price_mode !== "per_person" || it.addon_art === "mindestumsatz"
        ? undefined
        : it.addon_ersetzt_hauptmenue === true
          ? o?.hauptmenue_ersetzung?.zaehler?.[it.id]
          : (it.addon_gaeste_cents ?? undefined);
    const gaesteCents = Array.isArray(reihe) && reihe.length > 1 ? reihe : null;
    const art: ZubuchArt =
      it.addon_art === "mindestumsatz"
        ? "mindestumsatz"
        : gaesteCents
          ? "zaehler"
          : it.price_mode === "per_person"
          ? "alle"
          : Math.max(1, it.addon_max_qty ?? 1) > 1
            ? "zaehler"
            : "pauschal";
    const cents = gaesteCents ? gaesteCents[1]! : art === "alle" || art === "mindestumsatz" ? (zeile > 0 ? zeile : einheit) : einheit > 0 ? einheit : zeile;
    liste.push({
      id: it.id,
      item: it,
      art,
      cents,
      max: gaesteCents ? gaesteCents.length - 1 : art === "zaehler" ? Math.max(1, it.addon_max_qty ?? 1) : 1,
      ersetzt: it.addon_ersetzt_hauptmenue === true && art !== "pauschal" && art !== "mindestumsatz",
      ersetztGaeste: typeof it.addon_ersetzt_gaeste === "number" ? it.addon_ersetzt_gaeste : null,
      vatBp: typeof it.addon_vat_rate_bp === "number" ? it.addon_vat_rate_bp : null,
      gruppe: it.addon_gruppe ?? null,
      gaesteCents,
    });
  }
  return liste;
}

/** Ein Schalter auf der Seite: eine einzelne Zubuchung oder mehrere „und“-verbundene. */
export interface ZubuchSchalter {
  key: string;
  members: Zubuch[];
  /** Der Mindestumsatz des Schalters, falls einer dabei ist. */
  mindestumsatz: Zubuch | null;
}

export function schalterAus(zubuchungen: Zubuch[]): ZubuchSchalter[] {
  const liste: ZubuchSchalter[] = [];
  const nachGruppe = new Map<string, ZubuchSchalter>();
  for (const z of zubuchungen) {
    if (z.gruppe) {
      let s = nachGruppe.get(z.gruppe);
      if (!s) {
        s = { key: `gruppe-${z.gruppe}`, members: [], mindestumsatz: null };
        nachGruppe.set(z.gruppe, s);
        liste.push(s);
      }
      s.members.push(z);
      if (z.art === "mindestumsatz") s.mindestumsatz = z;
    } else {
      liste.push({ key: z.id, members: [z], mindestumsatz: z.art === "mindestumsatz" ? z : null });
    }
  }
  return liste;
}

/** Menge einer Zubuchung in der Auswahl (0 = nicht gewählt). */
export type Wahl = Record<string, number>;

export function menge(wahl: Wahl, z: Zubuch): number {
  return Math.max(0, Math.floor(wahl[z.id] ?? 0));
}

/** Betrag einer Zubuchung bei der gewählten Menge (Mindestumsatz zählt hier nicht: er ergänzt nur). */
export function zeilenbetrag(z: Zubuch, wahl: Wahl): number {
  const m = menge(wahl, z);
  if (m <= 0 || z.art === "mindestumsatz") return 0;
  // sql/759: Gäste-Zähler — der eingefrorene Betrag für m Gäste (nachgeschlagen, wie die Annahme).
  if (z.gaesteCents) return z.gaesteCents[Math.min(m, z.gaesteCents.length - 1)] ?? 0;
  return z.art === "alle" ? z.cents : m * z.cents;
}

/** Wie viele Gäste diese Zubuchung vom Hauptmenü abzieht (nur ersetzende). */
export function ersetzteGaeste(z: Zubuch, wahl: Wahl): number {
  const m = menge(wahl, z);
  if (m <= 0 || !z.ersetzt) return 0;
  return z.art === "alle" ? (z.ersetztGaeste ?? 0) : m;
}

export interface VatZeile {
  rateBp: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
}

export interface Rechnung {
  /** Gäste, die das Hauptmenü essen (Fassung minus ersetzte). */
  menuGaeste: number;
  ersetzt: number;
  /** Wahl überschreitet die Gästezahl (nur bei Fehlbedienung/Altstand): Annahme gesperrt. */
  ueberbucht: boolean;
  /** Betrag des Hauptmenüs nach Abzug. */
  menuCents: number;
  abzugCents: number;
  /** Summe der gewählten Zubuchungen (ohne Aufstockung). */
  zusatzCents: number;
  aufstockungCents: number;
  /** Mindestumsatz-Grenze (brutto, inkl. Menü) oder 0. */
  mindestumsatzCents: number;
  /** „Ihre Auswahl“ im Sinne des Mindestumsatzes: Menü + übrige Zubuchungen ohne Raummiete. */
  mindestumsatzIst: number;
  /** Unterschied zum Variantenpreis (kann negativ sein). */
  deltaCents: number;
  totalCents: number;
  /** Anzahl gewählter Zubuchungen (0 = nichts gewählt). */
  gewaehlt: number;
}

export type Mengen = Record<string, Record<string, number>>;

/**
 * sql/755 `wahl_betrag_aus_snapshot`, Zeile für Zeile gespiegelt: Summe (Menge × eingefrorener Preis) der
 * Alternativen plus Rest des Angebots, Rabatt mit denselben Ganzzahlen. `null`, solange der Gast keinen
 * Wahlgang angefasst hat (dann gilt der Grundbetrag der Variante) oder die Tabelle fehlt.
 */
export function wahlBetrag(
  o: AngebotOption,
  mengen: Mengen | undefined,
  k: number,
): { amountCents: number; summeCents: number; rabattCents: number; byVat: Map<number, number> } | null {
  const w = o.wahl;
  if (!w || !mengen) return null;
  // sql/772: eine Menüwahl zählt erst, wenn alle Gäste verteilt sind (Server: nur vollständige Wahlen gelten, sonst trägt das
  // günstigste Menü alle Gäste) - die halbe Verteilung am Bildschirm ändert den Betrag nicht.
  const halbeMenuewahl = (g: string): boolean => {
    const mw = o.menuewahlen?.[g];
    if (!mw) return false;
    const reihe = mengen[g] ?? {};
    return mw.ids.reduce((s, id) => s + Math.max(0, Math.floor(reihe[id] ?? 0)), 0) !== mw.soll;
  };
  const angefasst = Object.keys(w.gruppen).some((g) => !halbeMenuewahl(g) && (Object.values(mengen[g] ?? {}).some((n) => n > 0) || Object.keys(mengen[g] ?? {}).length > 0));
  if (!angefasst) return null;
  const kk = Math.max(0, Math.floor(k));
  const s = w.s[Math.min(kk, w.s.length - 1)] ?? 0;
  let d = w.d[Math.min(kk, w.d.length - 1)] ?? 0;
  let summe = 0;
  let rf = 0;
  const byVat = new Map<number, number>();
  for (const [id, a] of Object.entries(w.alternativen)) {
    const reihe = mengen[a.gruppe] ?? {};
    const angefasstG = Object.keys(reihe).length > 0 && !halbeMenuewahl(a.gruppe);
    const m = angefasstG ? Math.max(0, Math.floor(reihe[id] ?? 0)) : a.guenstigste ? Math.max(0, (a.gaeste ?? w.gaeste) - ((a.menue ?? true) ? kk : 0)) : 0;
    const zeile = a.proGastCents != null ? m * a.proGastCents : (a.tabelle?.[Math.min(m, (a.tabelle?.length ?? 1) - 1)] ?? 0);
    summe += zeile;
    if (a.rabattfaehig) rf += zeile;
    byVat.set(a.vatRateBp, (byVat.get(a.vatRateBp) ?? 0) + zeile);
  }
  d += rf;
  let disc = 0;
  if (w.rabattBp != null && w.rabattBp > 0) disc = Math.floor((d * w.rabattBp + 5000) / 10000);
  else if (w.rabattCents != null && w.rabattCents > 0) disc = w.rabattCents;
  disc = Math.max(0, Math.min(disc, d));
  return { amountCents: s + summe - disc, summeCents: summe, rabattCents: disc, byVat };
}

/** Betrag EINER Person bei dieser Alternative (Cent) oder `null`, wenn sie keine eingefrorene Zeile hat. */
export function alternativeJeGast(o: AngebotOption, itemId: string): number | null {
  const a = o.wahl?.alternativen[itemId];
  if (!a) return null;
  if (a.proGastCents != null) return a.proGastCents;
  return a.tabelle && a.tabelle.length > 1 ? a.tabelle[1]! : null;
}

/** „+ 4,00 € p. P.“: Aufpreis dieser Alternative gegenüber der günstigsten ihres Wahlgangs (0 = kein Aufpreis). */
export function alternativeAufpreis(o: AngebotOption, itemId: string): number {
  const a = o.wahl?.alternativen[itemId];
  const mein = alternativeJeGast(o, itemId);
  if (!a || mein == null) return 0;
  const ids = o.wahl?.gruppen[a.gruppe] ?? [];
  let min = mein;
  for (const id of ids) {
    const x = alternativeJeGast(o, id);
    if (x != null && x < min) min = x;
  }
  return mein - min;
}

export function rechne(o: AngebotOption, wahl: Wahl, mengen?: Mengen): Rechnung {
  const zub = zubuchungenAus(o);
  let zusatz = 0;
  let ersetzt = 0;
  let gewaehlt = 0;
  let mu = 0;
  let muGruppe: string | null = null;
  for (const z of zub) {
    if (menge(wahl, z) <= 0) continue;
    gewaehlt += 1;
    zusatz += zeilenbetrag(z, wahl);
    ersetzt += ersetzteGaeste(z, wahl);
    if (z.art === "mindestumsatz" && z.cents > mu) {
      mu = z.cents;
      muGruppe = z.gruppe;
    }
  }
  const tab = o.hauptmenue_ersetzung ?? null;
  const maxErsetzt = tab ? tab.gaeste : o.guest_count;
  const ueberbucht = ersetzt > maxErsetzt;
  const abzug = ersetzt > 0 && tab ? (tab.abzugCents[Math.min(ersetzt, tab.abzugCents.length - 1)] ?? 0) : 0;
  // sql/755: bei bepreisten Alternativen ersetzt der mengengenaue Wahlbetrag (Rest − Rabatt + Σ Menge × Preis) das Menü.
  const wb = wahlBetrag(o, mengen, ersetzt);
  const amount = wb ? wb.amountCents + zusatz : o.amount_total_cents + zusatz - abzug;
  // Raummiete & Co. (gleiche Gruppe wie der Mindestumsatz) zählen NICHT zum Mindestumsatz.
  let ex = 0;
  if (mu > 0 && muGruppe) {
    for (const z of zub) if (z.gruppe === muGruppe && z.art !== "mindestumsatz") ex += zeilenbetrag(z, wahl);
  }
  const ist = amount - ex;
  const aufst = mu > 0 ? Math.max(0, mu - ist) : 0;
  const total = amount + aufst;
  return {
    menuGaeste: Math.max(0, o.guest_count - ersetzt),
    ersetzt,
    ueberbucht,
    menuCents: wb ? wb.amountCents : o.amount_total_cents - abzug,
    abzugCents: abzug,
    zusatzCents: zusatz,
    aufstockungCents: aufst,
    mindestumsatzCents: mu,
    mindestumsatzIst: ist,
    deltaCents: total - o.amount_total_cents,
    totalCents: total,
    gewaehlt,
  };
}

/**
 * Enthaltene USt je Satz (Vorschau): Ausgangspunkt ist die Aufteilung der Variante, dazu kommen die
 * Zubuchungen und die Aufstockung mit ihrem eingefrorenen Satz, und der Abzug je Satz aus der
 * eingefrorenen Tabelle. `null`, wenn die Variante keine Aufteilung trägt (nichts zu zeigen).
 */
export function ustAufteilung(o: AngebotOption, wahl: Wahl, r: Rechnung, mengen?: Mengen): VatZeile[] | null {
  const basis = o.vat_breakdown;
  if (!basis || basis.length === 0) return null;
  const brutto = new Map<number, number>();
  for (const b of basis) brutto.set(b.rateBp, (brutto.get(b.rateBp) ?? 0) + b.grossCents);
  const stdSatz = basis[0]!.rateBp;
  const zub = zubuchungenAus(o);
  for (const z of zub) {
    const betrag = zeilenbetrag(z, wahl);
    if (betrag > 0) brutto.set(z.vatBp ?? stdSatz, (brutto.get(z.vatBp ?? stdSatz) ?? 0) + betrag);
  }
  if (r.aufstockungCents > 0) {
    // Inhaber-Entscheidung 27.10.2026: die Aufstockung rechnet IMMER mit 19 % USt, unabhängig von der Position.
    brutto.set(AUFSTOCKUNG_MINDESTUMSATZ_VAT_BP, (brutto.get(AUFSTOCKUNG_MINDESTUMSATZ_VAT_BP) ?? 0) + r.aufstockungCents);
  }
  // sql/755: Mengen der Alternativen gegenüber dem Grundbetrag (günstigste trägt alle) je Satz; ein Rest
  // (Rabatt-Rundung) geht auf den Hauptsatz, damit die Summe der Zeilen dem Gesamtbetrag entspricht.
  const wb = wahlBetrag(o, mengen, r.ersetzt);
  if (wb && o.wahl) {
    const grund = new Map<number, number>();
    let grundSumme = 0;
    for (const a of Object.values(o.wahl.alternativen)) {
      if (!a.guenstigste) continue;
      const m = Math.max(0, (a.gaeste ?? o.wahl.gaeste) - ((a.menue ?? true) ? r.ersetzt : 0));
      const z = a.proGastCents != null ? m * a.proGastCents : (a.tabelle?.[Math.min(m, a.tabelle.length - 1)] ?? 0);
      grund.set(a.vatRateBp, (grund.get(a.vatRateBp) ?? 0) + z);
      grundSumme += z;
    }
    let delta = 0;
    for (const satz of new Set([...grund.keys(), ...wb.byVat.keys()])) {
      const dz = (wb.byVat.get(satz) ?? 0) - (grund.get(satz) ?? 0);
      if (dz !== 0) brutto.set(satz, (brutto.get(satz) ?? 0) + dz);
      delta += dz;
    }
    const soll = wb.amountCents - (o.amount_total_cents - r.abzugCents);
    const rest = soll - delta;
    if (rest !== 0 && grundSumme >= 0) brutto.set(stdSatz, (brutto.get(stdSatz) ?? 0) + rest);
  }
  const tab = o.hauptmenue_ersetzung ?? null;
  if (r.ersetzt > 0 && tab) {
    const nachSatz = tab.abzugNachSatz;
    if (nachSatz && Object.keys(nachSatz).length > 0) {
      for (const [satz, reihe] of Object.entries(nachSatz)) {
        const abzug = reihe[Math.min(r.ersetzt, reihe.length - 1)] ?? 0;
        brutto.set(Number(satz), (brutto.get(Number(satz)) ?? 0) - abzug);
      }
    } else {
      brutto.set(stdSatz, (brutto.get(stdSatz) ?? 0) - r.abzugCents);
    }
  }
  return [...brutto.entries()]
    .filter(([, g]) => g !== 0)
    .sort(([a], [b]) => b - a)
    .map(([rateBp, grossCents]) => {
      const netCents = Math.round((grossCents * 10000) / (10000 + rateBp));
      return { rateBp, netCents, vatCents: grossCents - netCents, grossCents };
    });
}

/* ───────────── Wahlgänge (oder) mit Gäste-Zählern ───────────── */

export interface WahlgangGericht {
  id: string;
  item: PublicOfferItem;
  /** Höchstens so viele Gäste (Teilgruppe/Tageszahl der Alternative, `max` im Verzeichnis) - wie SQL. */
  max: number;
}

export interface Wahlgang {
  gruppeId: string;
  /** Gang-Bezeichnung (z. B. „Hauptgang“), falls gepflegt. */
  gang: string | null;
  gerichte: WahlgangGericht[];
  /** Gäste dieses Wahlgangs (mehrtägig: die Tageszahl), vor Abzug ersetzter Hauptmenü-Gäste. */
  gaeste: number;
  /** Hauptmenü-Wahlgang (Server: `wahlgangIstHauptmenue`, gruppenweit): nur dort senken ersetzende Zubuchungen die Soll-Summe. */
  menue: boolean;
}

/**
 * Soll-Summe eines Wahlgangs: seine Gäste, im Menü minus die Gäste, die eine ersetzende Zubuchung essen.
 * `menuGaeste` = Gäste der Variante minus ersetzte (wie `Rechnung.menuGaeste`), `optionGaeste` = Gäste der Variante.
 */
export function wahlSoll(w: Wahlgang, menuGaeste: number, optionGaeste: number): number {
  const ersetzt = Math.max(0, optionGaeste - menuGaeste);
  return Math.max(0, w.gaeste - (w.menue ? ersetzt : 0));
}

/**
 * Alle Wahlgänge einer Variante, in der Reihenfolge des Angebots - AUSSCHLIESSLICH aus dem beim Versand
 * eingefrorenen Verzeichnis (`o.wahlgaenge`, Server: `wahlgaengeSnapshot` über `wahlgaengeVerzeichnis`).
 * Fehlt es (Fassung/Option von vor sql/755), gibt es KEINE Wahlgang-Zähler und keine Annahmesperre - genau wie
 * der Server (zweite Prüfrunde, F-B1: keine zweite, eigene Definition auf der Gastseite).
 */
export function wahlgaengeAus(o: AngebotOption | null | undefined): Wahlgang[] {
  if (!o || !o.wahlgaenge) return [];
  const items = o.items ?? [];
  const nachId = new Map(items.map((it, i) => [it.id, { it, i }]));
  const verz = Object.entries(o.wahlgaenge)
    .map(([gruppe, w]) => ({ gruppe, ...w }))
    .sort((a, b) => (nachId.get(a.ids[0]!)?.i ?? 0) - (nachId.get(b.ids[0]!)?.i ?? 0));
  const liste: Wahlgang[] = [];
  for (const w of verz) {
    const gerichte = w.ids.flatMap((id) => {
      const x = nachId.get(id);
      // F-B5: je Alternative gedeckelt wie SQL (`max` im Verzeichnis; ältere Snapshots: die Preisliste, sonst die Soll-Gäste).
      const max = w.max?.[id] ?? o.wahl?.alternativen?.[id]?.gaeste ?? w.gaeste;
      return x ? [{ id, item: x.it, max }] : [];
    });
    if (gerichte.length > 1) liste.push({ gruppeId: w.gruppe, gang: w.gang, gerichte, gaeste: w.gaeste, menue: w.menue });
  }
  return liste;
}

export interface WahlgangLage {
  gruppeId: string;
  soll: number;
  summe: number;
  /** Noch zuzuordnende Gäste (≥ 0). */
  offen: number;
  /** Zu viel zugeordnete Gäste (≥ 0). */
  zuViel: number;
  ok: boolean;
}

export function wahlgangStand(w: Wahlgang, mengen: Mengen, soll: number): WahlgangLage {
  const reihe = mengen[w.gruppeId] ?? {};
  const summe = w.gerichte.reduce((s, g) => s + Math.max(0, Math.floor(reihe[g.id] ?? 0)), 0);
  return { gruppeId: w.gruppeId, soll, summe, offen: Math.max(0, soll - summe), zuViel: Math.max(0, summe - soll), ok: summe === soll };
}

/** Der Stand aller Wahlgänge der Variante bei `menuGaeste` Gästen mit Hauptmenü. */
export function wahlStand(o: AngebotOption | null | undefined, mengen: Mengen, menuGaeste: number): { gang: Wahlgang; stand: WahlgangLage }[] {
  return wahlgaengeAus(o).map((gang) => ({ gang, stand: wahlgangStand(gang, mengen, wahlSoll(gang, menuGaeste, o?.guest_count ?? menuGaeste)) }));
}

/** Die Zeile „15 × Dorade, 7 × Brasato“ eines Wahlgangs (nur Gerichte mit Menge > 0). */
export function wahlZeile(w: Wahlgang, mengen: Mengen, name: (it: PublicOfferItem) => string): string {
  const reihe = mengen[w.gruppeId] ?? {};
  return w.gerichte
    .filter((g) => (reihe[g.id] ?? 0) > 0)
    .map((g) => `${reihe[g.id]} × ${name(g.item)}`)
    .join(", ");
}

/**
 * Zusatz-Schutz (28.10.2026): eine Variante OHNE festen Preis - die Paket-Kopfzeile ist selbst zubuchbar und sonst
 * ist nichts bepreist, die Karte stünde bei „0,00 € pro Person“. Dann zeigt die Karte „Bitte unten wählen …“, und
 * „Angebot annehmen“ bleibt gesperrt, solange die Summe 0 € ist (der Server lehnt das ebenfalls ab: `nichts_gewaehlt`,
 * sql/763). „À la carte“ (Abrechnung vor Ort, Betrag 0 gewollt) ist ausgenommen.
 */
export function nurZubuchungen(o: AngebotOption | null | undefined, alaCarte: boolean): boolean {
  if (!o || alaCarte) return false;
  if ((o.amount_total_cents ?? 0) !== 0) return false;
  return zubuchungenAus(o).some((z) => z.art !== "mindestumsatz");
}

/**
 * sql/763 (Zweitprüfung B2): die Auswahl, wie sie an den Server geht (Annahme und Zahlungsvorschau). Bei einem
 * Gäste-Zähler einer NICHT ersetzenden per_person-Zubuchung geht zusätzlich `gaeste: k` mit (qty = k) - nur dann
 * rechnet der Server k Gäste; ohne `gaeste` gilt die alte Bedeutung „qty 1 = alle Gäste“ (alte Seite im Deploy-Fenster).
 * Menge 0 wird weggelassen. Reihenfolge nach Kennung, damit der Schlüssel der Vorschau stabil ist.
 */
export function zubuchungsAuswahl(o: AngebotOption | null | undefined, wahl: Wahl): { itemId: string; qty: number; gaeste?: number }[] {
  const zaehler = new Set(zubuchungenAus(o).filter((z) => z.gaesteCents && !z.ersetzt).map((z) => z.id));
  return Object.entries(wahl)
    .filter(([, qty]) => qty > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([itemId, qty]) => (zaehler.has(itemId) ? { itemId, qty, gaeste: qty } : { itemId, qty }));
}

/* ───────────── Menüwahl im Paket (sql/771/772): Gäste auf mehrere vollständige Menüs verteilen ───────────── */

/** Die drei Arten eines Menüs (Anzeige: Texte der Seite). */
export const MENUE_ARTEN_GAST = ["vegan", "vegetarisch", "fisch_fleisch"] as const;

export interface MenuewahlMenue {
  /** Kennung der Menü-Kopfzeile - der Schlüssel der Wahl `{ gruppe: { kopfzeile: anzahl } }`. */
  id: string;
  item: PublicOfferItem;
  art: string | null;
  /** Aufpreis je Gast in Cent (eingefroren, vom Server). */
  aufpreisCents: number;
  /** Die Gänge des Menüs (Bestandteile), in Angebotsreihenfolge. */
  bestandteile: PublicOfferItem[];
}

export interface Menuewahl {
  gruppeId: string;
  /** So viele Gäste verteilt der Gast. */
  soll: number;
  /** Darf die Fassung ohne vollständige Wahl angenommen werden (Wahl nach der Buchung)? */
  spaeter: boolean;
  menues: MenuewahlMenue[];
}

/**
 * Alle Menüwahlen einer Variante - AUSSCHLIESSLICH aus dem beim Versand eingefrorenen Verzeichnis (`o.menuewahlen`, wie die
 * Wahlgänge). Fehlt es oder hat die Gruppe weniger als zwei Menüs, gibt es keine Wahl: kein Zähler, keine Sperre.
 */
export function menuewahlenAus(o: AngebotOption | null | undefined): Menuewahl[] {
  if (!o || !o.menuewahlen) return [];
  const items = o.items ?? [];
  const nachId = new Map(items.map((it, i) => [it.id, { it, i }]));
  const liste: Menuewahl[] = [];
  const verz = Object.entries(o.menuewahlen).sort((a, b) => (nachId.get(a[1].ids[0]!)?.i ?? 0) - (nachId.get(b[1].ids[0]!)?.i ?? 0));
  for (const [gruppeId, v] of verz) {
    const menues: MenuewahlMenue[] = v.ids.flatMap((id) => {
      const x = nachId.get(id);
      if (!x) return [];
      const herkunft = x.it.paket_menue_id ?? null;
      const bestandteile = herkunft
        ? items.filter((it) => !it.menue_wahl_gruppe && it.paket_menue_id === herkunft && !it.is_optional && (it.package_id ?? null) === (x.it.package_id ?? null) && (it.day_no ?? null) === (x.it.day_no ?? null))
        : [];
      return [{ id, item: x.it, art: v.arten?.[id] ?? x.it.menue_art ?? null, aufpreisCents: Math.max(0, v.aufpreise?.[id] ?? 0), bestandteile }];
    });
    if (menues.length >= 2) liste.push({ gruppeId, soll: Math.max(0, Math.floor(v.soll)), spaeter: v.spaeter === true, menues });
  }
  return liste;
}

/** Alle Positionen, die zu den Menüs einer Wahl gehören (Kopfzeilen und Gänge) - sie stehen im Auswahl-Block, nicht in der Karte. */
export function menuewahlPositionen(o: AngebotOption | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const m of menuewahlenAus(o)) for (const x of m.menues) { ids.add(x.id); for (const b of x.bestandteile) ids.add(b.id); }
  return ids;
}

export interface MenuewahlLage {
  gruppeId: string;
  soll: number;
  summe: number;
  /** Noch zuzuordnende Gäste (≥ 0). */
  offen: number;
  /** Zu viel zugeordnete Gäste (≥ 0). */
  zuViel: number;
  ok: boolean;
  /** Noch nichts verteilt. */
  leer: boolean;
}

export function menuewahlLage(m: Menuewahl, mengen: Mengen): MenuewahlLage {
  const reihe = mengen[m.gruppeId] ?? {};
  const summe = m.menues.reduce((s, x) => s + Math.max(0, Math.floor(reihe[x.id] ?? 0)), 0);
  return { gruppeId: m.gruppeId, soll: m.soll, summe, offen: Math.max(0, m.soll - summe), zuViel: Math.max(0, summe - m.soll), ok: summe === m.soll, leer: summe === 0 };
}

/**
 * Hält die Annahme an? Eine Verteilung mit zu vielen Gästen immer; eine unvollständige nur, wenn die Fassung die Wahl nicht
 * auf später verschieben darf (`spaeter`). Dieselbe Regel wie der Server (`menueauswahl_unvollstaendig`, sql/772).
 */
export function menuewahlSperrt(m: Menuewahl, lage: MenuewahlLage): boolean {
  return lage.zuViel > 0 || (!lage.ok && !m.spaeter);
}

/** Die Zeile „3 × Verde (vegan), 3 × Orto (vegetarisch), 74 × Tradizione (Fisch/Fleisch)“ aus der Wahl; null, wenn nichts verteilt ist. */
export function menuewahlZeile(m: Menuewahl, mengen: Mengen, name: (it: PublicOfferItem) => string, artText: (art: string | null) => string | null): string | null {
  const reihe = mengen[m.gruppeId] ?? {};
  const teile = [...m.menues]
    .filter((x) => (reihe[x.id] ?? 0) > 0)
    .map((x) => `${reihe[x.id]} × ${name(x.item)}${artText(x.art) ? ` (${artText(x.art)})` : ""}`);
  return teile.length > 0 ? teile.join(", ") : null;
}

/** Standard-Frist der Wahl nach der Buchung (14 Tage vor dem Termin, nie vor heute) als Datum JJJJ-MM-TT; null ohne Termin. */
export function menuewahlStandardFrist(eventDate: string | null | undefined, heute: Date = new Date()): string | null {
  if (!eventDate || !/^\d{4}-\d{2}-\d{2}/.test(eventDate)) return null;
  const tag = (d: Date) => d.toISOString().slice(0, 10);
  const f = new Date(`${eventDate.slice(0, 10)}T00:00:00Z`);
  f.setUTCDate(f.getUTCDate() - 14);
  return tag(f) < tag(heute) ? tag(heute) : tag(f);
}
